/**
 * Data Access Layer for Auth Events
 *
 * Logs authentication events for security auditing and usage tracking.
 * Captures login successes and failures with contextual information.
 */

import { getClient, getAdminClient } from './client.js';
import { TABLES } from './tables.js';
import type { AuthEvent, AuthEventCreate } from './types.js';

/**
 * auth_events is RLS-locked to service_role only (migration 082): it holds a
 * security audit log (emails, IPs, failure reasons) that must never be readable
 * or writable via the public anon key. All access goes through the service-role
 * admin client, which bypasses RLS. Falls back to the normal client only when no
 * service-role key is configured (local / in-memory dev, where RLS isn't live).
 */
function authEventsClient() {
  return getAdminClient() ?? getClient();
}

/**
 * Create an auth event log entry
 */
export async function createAuthEvent(input: AuthEventCreate): Promise<AuthEvent> {
  const { data, error } = await authEventsClient()
    .from(TABLES.auth_events)
    .insert({
      account_id: input.account_id ?? null,
      user_id: input.user_id ?? null,
      email: input.email.toLowerCase(),
      event_type: input.event_type,
      failure_reason: input.failure_reason ?? null,
      ip_address: input.ip_address ?? null,
      user_agent: input.user_agent ?? null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data as AuthEvent;
}

/**
 * List auth events with optional filters
 */
export async function listAuthEvents(filters?: {
  email?: string;
  event_type?: 'login_success' | 'login_failed' | 'session_resumed';
  user_id?: string;
  ip_address?: string;
  since?: Date;
  limit?: number;
}): Promise<{ data: AuthEvent[]; count: number }> {
  let query = authEventsClient()
    .from(TABLES.auth_events)
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false });

  if (filters?.email) {
    query = query.eq('email', filters.email.toLowerCase());
  }
  if (filters?.event_type) {
    query = query.eq('event_type', filters.event_type);
  }
  if (filters?.user_id) {
    query = query.eq('user_id', filters.user_id);
  }
  if (filters?.ip_address) {
    query = query.eq('ip_address', filters.ip_address);
  }
  if (filters?.since) {
    query = query.gte('created_at', filters.since.toISOString());
  }
  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  const { data, count, error } = await query;

  if (error) throw error;
  return { data: (data || []) as AuthEvent[], count: count || 0 };
}

/**
 * Get count of recent failed login attempts for a specific email
 * Useful for detecting brute force attacks
 */
export async function getRecentFailedAttempts(
  email: string,
  minutes: number = 15
): Promise<number> {
  const since = new Date(Date.now() - minutes * 60 * 1000);

  const { count, error } = await authEventsClient()
    .from(TABLES.auth_events)
    .select('*', { count: 'exact', head: true })
    .eq('email', email.toLowerCase())
    .eq('event_type', 'login_failed')
    .gte('created_at', since.toISOString());

  if (error) throw error;
  return count || 0;
}

/**
 * Get count of recent failed login attempts from a specific IP
 * Useful for detecting credential stuffing attacks
 */
export async function getRecentFailedAttemptsByIp(
  ipAddress: string,
  minutes: number = 15
): Promise<number> {
  const since = new Date(Date.now() - minutes * 60 * 1000);

  const { count, error } = await authEventsClient()
    .from(TABLES.auth_events)
    .select('*', { count: 'exact', head: true })
    .eq('ip_address', ipAddress)
    .eq('event_type', 'login_failed')
    .gte('created_at', since.toISOString());

  if (error) throw error;
  return count || 0;
}

/**
 * Delete old auth events based on retention policy
 * - login_failed: 365 days (keep longer for security auditing)
 * - session_resumed: 30 days (high volume, less critical)
 * - login_success: 90 days (default)
 *
 * Returns the number of deleted records
 */
export async function deleteOldAuthEvents(): Promise<{
  login_failed: number;
  session_resumed: number;
  login_success: number;
}> {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;

  const cutoffs = {
    login_failed: new Date(now - 365 * day).toISOString(),
    session_resumed: new Date(now - 30 * day).toISOString(),
    login_success: new Date(now - 90 * day).toISOString(),
  };

  const results = {
    login_failed: 0,
    session_resumed: 0,
    login_success: 0,
  };

  for (const [eventType, cutoff] of Object.entries(cutoffs)) {
    const { data, error } = await authEventsClient()
      .from(TABLES.auth_events)
      .delete()
      .eq('event_type', eventType)
      .lt('created_at', cutoff)
      .select('id');

    if (error) {
      console.error(`Failed to delete old ${eventType} events:`, error);
      continue;
    }

    results[eventType as keyof typeof results] = data?.length || 0;
  }

  return results;
}
